import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { parseLocalApiOverride } from '../../../utils/parseLocalApiOverride';
import { isOAuth, isOAuthWithPolling } from '../../../utils/oauthHelpers';
import { useLowLevelAuthFlowMachine } from '../LowLevelAuthFlowMachineContext';
import { useOAuthWindow } from './useOAuthWindow';
import { useSessionPolling } from './useSessionPolling';

// Once polling begins the popup is no longer an observable signal, so we show
// the spinner for this long and then hand off to silent background polling.
// This is measured against the wall clock rather than a poll count, which
// drifts from real time with network latency and timer throttling.
const SPINNER_VISIBLE_MS = 5000;
const isConvertibleAuthError = error => typeof error === 'object' && error !== null && 'toAuthError' in error && typeof error.toAuthError === 'function';
export function useFinishAuth() {
  const {
    state,
    send
  } = useLowLevelAuthFlowMachine();
  const {
    portalId,
    authConfig,
    params,
    authProcessStatus,
    oAuthState,
    oAuthScopeOverride,
    customParamsOverride
  } = state.context;
  const {
    appName,
    appId,
    authFlow,
    mcpParams,
    oAuthScopeSet,
    customParams,
    buildAuthPayload,
    makeAuthorizeUrlDescriptor,
    finishAuthFlow
  } = authConfig;
  const {
    sessionId,
    type: authFlowType,
    steps
  } = authFlow;
  const handleFailure = useCallback(error => {
    send({
      type: 'FINISH_FAILURE',
      error
    });
  }, [send]);
  const handleSuccess = useCallback(response => {
    send({
      type: 'FINISH_SUCCESS',
      response
    });
  }, [send]);
  const executeAuthFlow = useCallback(async authContext => {
    try {
      const payload = await buildAuthPayload(authContext);
      const response = await finishAuthFlow(payload);
      handleSuccess(response);
    } catch (err) {
      var _error$responseJSON;
      const error = err;
      if (isConvertibleAuthError(err)) {
        handleFailure(err.toAuthError());
      } else if ((_error$responseJSON = error.responseJSON) !== null && _error$responseJSON !== void 0 && _error$responseJSON.message) {
        handleFailure({
          type: 'AuthenticateError',
          message: error.responseJSON.message
        });
      } else {
        handleFailure({
          type: 'AuthenticateError',
          message: error.message
        });
      }
    }
  }, [buildAuthPayload, finishAuthFlow, handleSuccess, handleFailure]);
  const isFinishingRef = useRef(false);
  const handleOAuthResponse = useCallback((oAuthParams, receivedState) => {
    if (receivedState !== oAuthState) {
      handleFailure({
        type: 'UnknownOAuthProblem'
      });
      return;
    }

    // Guards against both the window-message and polling paths firing
    // simultaneously. Reset happens in startAuth on each new attempt.
    if (isFinishingRef.current) {
      return;
    }
    isFinishingRef.current = true;
    send({
      type: 'OAUTH_RESPONSE_RECEIVED',
      params: oAuthParams,
      state: receivedState
    });
    void executeAuthFlow({
      sessionId,
      steps,
      urlParams: params,
      oAuthParams,
      oAuthState: receivedState,
      oAuthScopeSet: oAuthScopeOverride !== null && oAuthScopeOverride !== void 0 ? oAuthScopeOverride : oAuthScopeSet,
      customParams: customParamsOverride !== null && customParamsOverride !== void 0 ? customParamsOverride : customParams,
      mcpParams,
      appId
    });
  }, [oAuthState, sessionId, steps, params, oAuthScopeOverride, oAuthScopeSet, customParamsOverride, customParams, mcpParams, appId, executeAuthFlow, handleFailure, send]);
  const oAuthCallbacks = useMemo(() => ({
    onWindowOpened: (oAuthWindow, oAuthStateValue) => {
      send({
        type: 'OAUTH_WINDOW_OPENED',
        window: oAuthWindow,
        oAuthState: oAuthStateValue
      });
      if (isOAuthWithPolling(authFlowType)) {
        send({
          type: 'START_POLLING'
        });
      }
    },
    onWindowBlocked: () => {
      send({
        type: 'OAUTH_WINDOW_BLOCKED'
      });
    },
    onWindowClosed: () => {
      send({
        type: 'OAUTH_WINDOW_CLOSED'
      });
    },
    onWindowTimeout: () => {
      send({
        type: 'OAUTH_WINDOW_TIMEOUT'
      });
    },
    onAuthResponse: (oAuthParams, status) => {
      if (status === 'AUTH_RESPONSE') {
        const receivedState = oAuthParams.get('state') || '';
        handleOAuthResponse(oAuthParams, receivedState);
      }
    },
    onAuthError: message => {
      handleFailure({
        type: 'AuthorizeError',
        message
      });
    }
  }), [authFlowType, handleFailure, handleOAuthResponse, send]);
  const {
    openWindow
  } = useOAuthWindow({
    appName,
    callbacks: oAuthCallbacks,
    enabled: authProcessStatus === 'authorizing' || authProcessStatus === 'polling'
  });
  const pollingCallbacks = useMemo(() => ({
    onPollTick: () => {
      send({
        type: 'POLL_TICK'
      });
    },
    onPollSuccess: (pollParams, status) => {
      const stateParam = pollParams.get('state') || sessionId;
      send({
        type: 'POLL_SUCCESS',
        params: pollParams,
        status
      });
      if (status !== 'PENDING') {
        handleOAuthResponse(pollParams, stateParam);
      }
    },
    onPollError: () => {
      send({
        type: 'POLL_ERROR'
      });
    },
    onTimeout: () => {
      send({
        type: 'OAUTH_WINDOW_TIMEOUT'
      });
    }
  }), [handleOAuthResponse, send, sessionId]);
  useSessionPolling({
    portalId,
    sessionId,
    callbacks: pollingCallbacks,
    enabled: authProcessStatus === 'polling'
  });
  const startAuth = useCallback(oAuthWindow => {
    isFinishingRef.current = false;
    send({
      type: 'START_AUTH'
    });
    if (isOAuth(authFlowType)) {
      const executeOAuthFlow = async () => {
        try {
          const authPayload = await buildAuthPayload({
            sessionId,
            steps,
            urlParams: params,
            oAuthScopeSet: oAuthScopeOverride !== null && oAuthScopeOverride !== void 0 ? oAuthScopeOverride : oAuthScopeSet,
            customParams: customParamsOverride !== null && customParamsOverride !== void 0 ? customParamsOverride : customParams,
            oAuthState: sessionId,
            oAuthParams: new Map(),
            mcpParams,
            appId
          });
          const {
            authorizeUrl
          } = await makeAuthorizeUrlDescriptor(authPayload);
          const parsedUrl = parseLocalApiOverride(authorizeUrl);
          openWindow(sessionId, parsedUrl, oAuthWindow);
        } catch (err) {
          if (isConvertibleAuthError(err)) {
            handleFailure(err.toAuthError());
          } else {
            var _error$responseJSON2;
            const error = err;
            handleFailure({
              type: 'AuthorizeError',
              message: ((_error$responseJSON2 = error.responseJSON) === null || _error$responseJSON2 === void 0 ? void 0 : _error$responseJSON2.message) || error.message
            });
          }
        }
      };
      void executeOAuthFlow();
    } else {
      void executeAuthFlow({
        sessionId,
        steps,
        urlParams: params,
        customParams: customParamsOverride !== null && customParamsOverride !== void 0 ? customParamsOverride : customParams,
        appId
      });
    }
  }, [send, authFlowType, sessionId, steps, params, oAuthScopeOverride, oAuthScopeSet, customParamsOverride, customParams, mcpParams, appId, buildAuthPayload, makeAuthorizeUrlDescriptor, openWindow, executeAuthFlow, handleFailure]);
  const isActivelyAuthing = authProcessStatus === 'authorizing' || authProcessStatus === 'authenticating' || authProcessStatus === 'polling';

  // In a polling flow the popup closing isn't an observable signal, so rather
  // than spinning until the session times out we show the spinner for a fixed
  // window of real time once polling starts, then keep polling silently in the
  // background in case the user is still completing auth. Non-polling flows
  // never enter the polling state, so their spinner stays up for the whole flow.
  const [pollingSpinnerExpired, setPollingSpinnerExpired] = useState(false);
  useEffect(() => {
    if (authProcessStatus !== 'polling') {
      setPollingSpinnerExpired(false);
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      setPollingSpinnerExpired(true);
    }, SPINNER_VISIBLE_MS);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [authProcessStatus]);
  const isLoading = isActivelyAuthing && !pollingSpinnerExpired;
  const hasFailed = authProcessStatus === 'failed' || authProcessStatus === 'cancelled' ||
  // Once the spinner stops while polling continues we can't confirm success,
  // so keep the button out of its completed (checkmark) state.
  isActivelyAuthing && !isLoading;
  return {
    startAuth,
    isLoading,
    hasFailed
  };
}